import { IOperationOcrDetail, IProcessDetailResponse, PROCESS_STATUS } from '../../processes/models/processes.models';
import {
	IFormOperationAnswerStep,
	IFormStepActivityEvent,
	IOperationFormDetail,
	ITextLocaleMap,
	OPERATION_STATUS,
} from '../models/operations.models';
import { hasFormFieldMatchOcrKo } from './form-operation-detail-display.util';
import {
	buildFormMatchDataErrorKeySet,
	hasFormStepActivityKo,
	resolveFormMatchDataErrors,
} from './form-operation-match-data.util';
import { listFormMatchCompareFields } from './form-operation-match-compare.util';

/** Proyecta `detail.data` al contrato FORM en detalle de operación/proceso. */
export function asFormOperationDetail(data: unknown): IOperationFormDetail | null {
	if (!data || typeof data !== 'object') {
		return null;
	}
	return data as IOperationFormDetail;
}

/** Snapshot OCR embebido en `data.checks.ocr` (admin_back). */
export function resolveFormOperationOcrPayload(
	form: IOperationFormDetail | null | undefined,
): IOperationOcrDetail | null {
	const ocr = form?.checks?.ocr;
	if (!ocr || typeof ocr !== 'object') {
		return null;
	}
	return ocr as unknown as IOperationOcrDetail;
}

export function resolveFormOperationOcrOperationUuid(form: IOperationFormDetail | null | undefined): string | null {
	const uuid = form?.checks?.ocr_operation_uuid?.trim();
	return uuid || null;
}

export function hasFormOperationOcrChecks(form: IOperationFormDetail | null | undefined): boolean {
	return resolveFormOperationOcrPayload(form) !== null;
}

/** Snapshot OCR de FORM para `app-checks` en detalle de proceso (mismo patrón que `detail-result-tile`). */
export function buildFormChecksProcessDetail(
	process: IProcessDetailResponse | null | undefined,
): IProcessDetailResponse | null {
	const form = asFormOperationDetail(process?.data?.FORM);
	if (!form || !process) {
		return null;
	}
	const ocr = resolveFormOperationOcrPayload(form);
	if (!ocr && !listFormMatchCompareFields(form).length) {
		return null;
	}
	const data: Partial<IProcessDetailResponse['data']> = { FORM: form };
	if (ocr) {
		data.OCR = ocr;
	}
	return {
		...process,
		data: data as IProcessDetailResponse['data'],
	};
}

/** Snapshot FORM (+ OCR opcional) para comprobaciones / resultado en detalle de operación. */
export function buildFormOperationChecksDetail(
	detail: { process_uuid?: string; locks?: IProcessDetailResponse['locks']; data?: unknown } | null | undefined,
): IProcessDetailResponse | null {
	const form = asFormOperationDetail(detail?.data);
	if (!form) {
		return null;
	}
	const ocr = resolveFormOperationOcrPayload(form);
	const data: Partial<IProcessDetailResponse['data']> = { FORM: form };
	if (ocr) {
		data.OCR = ocr;
	}
	return {
		general: {
			profile: { uuid: '', alias: '' },
			sms: [],
			auth: '',
			company: '',
			company_uuid: '',
			location: [],
			devices: [],
			init_operation: '',
			total_time: 0,
			end_operation: '',
			validation: '',
		},
		process_uuid: detail?.process_uuid ?? '',
		process_details: '',
		status_process: PROCESS_STATUS.KO,
		data: data as IProcessDetailResponse['data'],
		locks: detail?.locks,
	} as IProcessDetailResponse;
}

export function hasFormChecksInProcess(process: IProcessDetailResponse | null | undefined): boolean {
	const form = asFormOperationDetail(process?.data?.FORM);
	return hasFormOperationOcrChecks(form) || listFormMatchCompareFields(form).length > 0;
}

/**
 * Pasos para timeline de actividad: prioriza `step_activity` del back;
 * si no existe, deriva entradas desde `answers[]` (sin fechas).
 */
export function resolveFormStepActivityEvents(form: IOperationFormDetail | null | undefined): IFormStepActivityEvent[] {
	if (!form) {
		return [];
	}
	if (form.step_activity?.length) {
		const errorKeys = buildFormMatchDataErrorKeySet(resolveFormMatchDataErrors(form));
		return [...form.step_activity]
			.map((step) => enrichStepActivityEvent(step, errorKeys))
			.sort((a, b) => a.order - b.order);
	}
	const errorKeys = buildFormMatchDataErrorKeySet(resolveFormMatchDataErrors(form));
	return [...(form.answers ?? [])]
		.map((answer) => buildStepActivityFromAnswer(answer, errorKeys))
		.sort((a, b) => a.order - b.order);
}

function enrichStepActivityEvent(
	step: IFormStepActivityEvent,
	errorKeys: Set<string>,
): IFormStepActivityEvent {
	const snapshot = step.response_snapshot as IFormOperationAnswerStep | undefined;
	if (!snapshot || !hasFormStepActivityKo(snapshot, errorKeys)) {
		return step;
	}
	if (String(step.state ?? '').toUpperCase() === OPERATION_STATUS.KO) {
		return step;
	}
	return { ...step, state: OPERATION_STATUS.KO };
}

function buildStepActivityFromAnswer(
	answer: IFormOperationAnswerStep,
	errorKeys: Set<string>,
): IFormStepActivityEvent {
	const state =
		hasFormFieldMatchOcrKo(answer.fields) || hasFormStepActivityKo(answer, errorKeys)
			? OPERATION_STATUS.KO
			: undefined;
	return {
		step_uuid: answer.uuid,
		order: answer.order,
		title: answer.title,
		completed_at: null,
		state,
		response_snapshot: answer,
	};
}

export function resolveFormStepActivityTitle(
	title: string | ITextLocaleMap | null | undefined,
	locale: string,
): string {
	if (!title) {
		return '';
	}
	if (typeof title === 'string') {
		return title;
	}
	const map = title as ITextLocaleMap;
	return map[locale] ?? map['es'] ?? map['en'] ?? Object.values(map).find((v) => !!v?.trim()) ?? '';
}
